import React, { useState, useEffect, useRef } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { PopupButton } from 'react-calendly';
import { Link, useLocation } from 'react-router-dom';

const Header = ({ scrolled, isSubPage = false }) => {
  const location = useLocation();
  const isOnSubPage = isSubPage || location.pathname !== '/';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [headerOpacity, setHeaderOpacity] = useState(isOnSubPage ? 1 : 0);
  const [showHeader, setShowHeader] = useState(isOnSubPage);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    // Auf Unterseiten: Header sofort anzeigen
    if (isOnSubPage) {
      setShowHeader(true);
      setHeaderOpacity(1);
      return;
    }
    
    // Header erscheint nach 3,5 Sekunden (2s Intro + 1,5s Fade)
    const showTimer = setTimeout(() => {
      setShowHeader(true);
    }, 3500);

    return () => clearTimeout(showTimer);
  }, [isOnSubPage]);

  useEffect(() => {
    // Auf Unterseiten: Scroll-Verhalten deaktivieren
    if (isOnSubPage) return;
    
    if (!showHeader) return;

    const updateOpacity = () => {
      const scrollY = window.scrollY;
      const scrollDirection = scrollY > lastScrollY.current ? 'down' : 'up';
      lastScrollY.current = scrollY;

      // Bei ScrollY = 0: Vollständig sichtbar
      if (scrollY === 0) {
        setHeaderOpacity(1);
      } 
      // Beim Runterscrollen: Progressiv ausfaden (0-200px)
      else if (scrollDirection === 'down') {
        const fadeDistance = 200;
        const opacity = Math.max(0, 1 - (scrollY / fadeDistance));
        setHeaderOpacity(opacity);
      }
      // Beim Hochscrollen: Nur wenn ganz oben, sonst bleibt es ausgeblendet
      else if (scrollDirection === 'up') {
        if (scrollY < 50) {
          const opacity = Math.min(1, (50 - scrollY) / 50);
          setHeaderOpacity(opacity);
        }
      }

      ticking.current = false;
    };

    const handleScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(updateOpacity);
        ticking.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showHeader, isOnSubPage]);

  const scrollToSection = (id) => {
    // Wenn auf Unterseite, zur Startseite navigieren mit Anker
    if (isOnSubPage) {
      window.location.href = `/#${id}`;
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  // Header startet unsichtbar, blendet dann ein (nur auf Startseite)
  const headerStyle = isOnSubPage 
    ? { opacity: 1, backgroundColor: 'rgb(17, 24, 39)' }
    : {
        opacity: showHeader ? headerOpacity : 0,
        transition: showHeader ? 'opacity 0.3s ease-out' : 'opacity 1.5s ease-in-out',
        pointerEvents: headerOpacity < 0.1 ? 'none' : 'auto'
      };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 ${isOnSubPage ? 'bg-gray-900 shadow-lg' : 'bg-transparent'}`}
      style={headerStyle}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo - größer (80-100px) */}
          <div className="flex items-center">
            <Link to="/">
              <img 
                src="https://customer-assets.emergentagent.com/job_srcglobal-web/artifacts/h90h1gk3_src-global-white.svg" 
                alt="SRC Global" 
                className="h-20 md:h-24 w-auto"
                style={{ imageRendering: 'crisp-edges' }}
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/team"
              className="text-sm font-medium text-white hover:text-green-400 transition-colors"
            >
              Team
            </Link>

            <a
              href="https://www.fedlex.admin.ch/eli/cc/2023/655/de"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-white hover:text-green-400 transition-colors"
            >
              Klimaschutzgesetz
            </a>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-sm font-medium text-white hover:text-green-400 transition-colors"
            >
              Kontakt
            </button>
            <PopupButton
              url="https://calendly.com/srcglobal/30min"
              rootElement={document.getElementById('root')}
              text="Jetzt Termin buchen"
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-md font-medium transition-colors"
            />
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-white"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <nav className="md:hidden py-4 bg-white rounded-lg shadow-lg mt-2">
            <div className="flex flex-col space-y-4 px-4">
              <Link
                to="/team"
                className="text-left text-gray-700 hover:text-green-600 font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Team
              </Link>

              <a
                href="https://www.fedlex.admin.ch/eli/cc/2023/655/de"
                target="_blank"
                rel="noopener noreferrer"
                className="text-left text-gray-700 hover:text-green-600 font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Klimaschutzgesetz
              </a>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-left text-gray-700 hover:text-green-600 font-medium"
              >
                Kontakt
              </button>
              <PopupButton
                url="https://calendly.com/srcglobal/30min"
                rootElement={document.getElementById('root')}
                text="Jetzt Termin buchen"
                className="bg-green-600 hover:bg-green-700 text-white w-full px-6 py-2 rounded-md font-medium transition-colors"
              />
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;